import moment from 'moment';
import posthogJs from 'posthog-js';

import {
  AUTO_LESSON_ANALYSIS_FEATURE_FLAG,
  TAutoAnalysisSurface,
} from '../constants/autoAnalysis';

const timeFormat = 'YYYY-MM-DD HH:mm:ss';
const timestamp = moment().format(timeFormat);

/**
 * Feature flags that should be re-evaluated for the identified user rather than
 * carried over from the anonymous session when calling `posthog.identify`.
 */
export const ANON_FLAGS_DELETE_LIST: string[] = [];

export const postHogTestBuilderShown = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('test_builder');
    posthog.capture('test_builder_shown', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogTestBuilderClicked = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('test_builder');
    posthog.capture('test_builder_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogTutorProfileBookingClicked = (posthog, data) => {
  posthog.capture('tutor_profile_booking_clicked', {
    ...data,
    timestamp,
  });
};

export const postHogTutorStrengthModalShown = (posthog, data) => {
  posthog.capture('tutor_strength_modal_shown', {
    ...data,
    timestamp,
  });
};

export const postHogTutorStrengthModalSubmitted = (posthog, data) => {
  posthog.capture('tutor_strength_modal_submitted', {
    ...data,
    timestamp,
  });
};

export const postHogBookTrialClassCtaShown = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('book_trial_class_cta');
    posthog.capture('book_trial_class_cta_shown', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogBookTrialClassCtaClicked = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('book_trial_class_cta');
    posthog.capture('book_trial_class_cta_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogTopicsCoveredButtonShown = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('trial-resource-button-validation');
    posthog.capture('topics_covered_button_shown', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogTopicsCoveredButtonClicked = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('trial-resource-button-validation');
    posthog.capture('topics_covered_button_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogTopicsCoveredModalOkayClicked = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('trial-resource-button-validation');
    posthog.capture('topics_covered_modal_okay_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogTopicsCoveredModalClosed = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('trial-resource-button-validation');
    posthog.capture('topics_covered_modal_closed', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogUniversityApplicationBannerShown = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('university_application_banner');
    posthog.capture('university_application_banner_shown', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogUniversityApplicationBannerClicked = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('university_application_banner');
    posthog.capture('university_application_banner_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogUniversityApplicationBannerDismissed = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('university_application_banner');
    posthog.capture('university_application_banner_dismissed', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogUploadButtonShown = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('Upload-button');
    posthog.capture('upload_button_shown', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogUploadButtonClicked = (posthog, data) => {
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('Upload-button');
    posthog.capture('upload_button_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogAiInstantSupportClicked = (posthog, data) => {
  posthog.capture('ai_instant_support_clicked', {
    ...data,
    timestamp,
  });
};

export const postHogWhatsAppSupportClicked = (posthog, data) => {
  posthog.capture('whatsapp_support_clicked', {
    ...data,
    timestamp,
  });
};

export const postHogAiInstantSupportMessageSent = (posthog, data) => {
  posthog.capture('ai_instant_support_message_sent', {
    ...data,
    timestamp,
  });
};

/** PostHog payloads use snake_case property names */
export const postHogTrialCancelModalViewed = (
  posthog,
  data: { classId: string; studentId: string }
) => {
  posthog.capture('trial_cancel_modal_viewed', {
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};

export const postHogTrialRescheduleModalViewed = (
  posthog,
  data: { classId: string; studentId: string }
) => {
  posthog.capture('trial_reschedule_modal_viewed', {
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};

export const postHogTrialCancelReasonSelected = (
  posthog,
  data: {
    mainReason: string;
    subReason: string;
    otherText?: string;
    classId: string;
    studentId: string;
  }
) => {
  posthog.capture('trial_cancel_reason_selected', {
    main_reason: data.mainReason,
    sub_reason: data.subReason,
    ...(data.otherText !== undefined ? { other_text: data.otherText } : {}),
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};
export const postHogTrialRescheduleReasonSelected = (
  posthog,
  data: {
    mainReason: string;
    subReason: string;
    otherText?: string;
    classId: string;
    studentId: string;
  }
) => {
  posthog.capture('trial_reschedule_reason_selected', {
    main_reason: data.mainReason,
    sub_reason: data.subReason,
    ...(data.otherText !== undefined ? { other_text: data.otherText } : {}),
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};

export const postHogTrialCancelQuickReasonSelected = (
  posthog,
  data: {
    quickReasonId: string;
    quickReasonLabel: string;
    classId: string;
    studentId: string;
  }
) => {
  posthog.capture('trial_cancel_quick_reason_selected', {
    quick_reason_id: data.quickReasonId,
    quick_reason_label: data.quickReasonLabel,
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};

export const postHogTrialCancelRescheduleOffered = (
  posthog,
  data: {
    quickReasonId: string;
    quickReasonLabel: string;
    classId: string;
    studentId: string;
  }
) => {
  posthog.capture('trial_cancel_reschedule_offered', {
    quick_reason_id: data.quickReasonId,
    quick_reason_label: data.quickReasonLabel,
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};

export const postHogTrialCancelRescheduleAccepted = (
  posthog,
  data: {
    quickReasonId: string;
    quickReasonLabel: string;
    classId: string;
    studentId: string;
  }
) => {
  posthog.capture('trial_cancel_reschedule_accepted', {
    quick_reason_id: data.quickReasonId,
    quick_reason_label: data.quickReasonLabel,
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};

export const postHogTrialCancelRescheduleDeclined = (
  posthog,
  data: {
    quickReasonId: string;
    quickReasonLabel: string;
    classId: string;
    studentId: string;
  }
) => {
  posthog.capture('trial_cancel_reschedule_declined', {
    quick_reason_id: data.quickReasonId,
    quick_reason_label: data.quickReasonLabel,
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};

export const postHogTrialCancelSubmitted = (
  posthog,
  data: { classId: string; studentId: string }
) => {
  posthog.capture('trial_cancel_submitted', {
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};

export const postHogTrialRescheduleSubmitted = (
  posthog,
  data: { classId: string; studentId: string }
) => {
  posthog.capture('trial_reschedule_submitted', {
    class_id: data.classId,
    student_id: data.studentId,
    timestamp,
  });
};

export const postHogMissedTrialBookAgainClicked = (
  posthog,
  data: {
    userId: string;
    classId: string;
    curriculum?: string;
    subject?: string;
    grade?: string;
  }
) => {
  posthog.capture('missed_trial_book_again_clicked', {
    user_id: data.userId,
    class_id: data.classId,
    ...(data.curriculum ? { curriculum: data.curriculum } : {}),
    ...(data.subject ? { subject: data.subject } : {}),
    ...(data.grade ? { grade: data.grade } : {}),
    timestamp,
  });
};

export const postHogCancelledTrialBookAgainClicked = (
  posthog,
  data: {
    userId: string;
    classId: string;
    curriculum?: string;
    subject?: string;
    grade?: string;
  }
) => {
  posthog.capture('cancelled_trial_book_again_clicked', {
    user_id: data.userId,
    class_id: data.classId,
    ...(data.curriculum ? { curriculum: data.curriculum } : {}),
    ...(data.subject ? { subject: data.subject } : {}),
    ...(data.grade ? { grade: data.grade } : {}),
    timestamp,
  });
};

export const postHogRateYourTutorCtaClicked = (
  posthog,
  data: { userId: string; courseId: string }
) => {
  if (!posthog) return;
  posthog.capture('rate_your_tutor_cta_clicked', {
    user_id: data.userId,
    course_id: data.courseId,
    timestamp,
  });
};

export const postHogRateYourTutorModalOpened = (
  posthog,
  data: { userId: string; courseId: string; trigger: 'cta' | 'auto' }
) => {
  if (!posthog) return;
  posthog.capture('rate_your_tutor_modal_opened', {
    user_id: data.userId,
    course_id: data.courseId,
    trigger: data.trigger,
    timestamp,
  });
};

export const postHogRateYourTutorModalSubmitted = (
  posthog,
  data: { userId: string; courseId: string; rating: number }
) => {
  if (!posthog) return;
  posthog.capture('rate_your_tutor_modal_submitted', {
    user_id: data.userId,
    course_id: data.courseId,
    rating: data.rating,
    timestamp,
  });
};

export const postHogRateYourTutorModalClosed = (
  posthog,
  data: { userId: string; courseId: string }
) => {
  if (!posthog) return;
  posthog.capture('rate_your_tutor_modal_closed', {
    user_id: data.userId,
    course_id: data.courseId,
    timestamp,
  });
};

/** User opened the Profile page (any tab). */
export const postHogProfilePageViewed = (
  posthog,
  data?: Record<string, unknown>
) => {
  if (!posthog) return;
  posthog.capture('profile_page_viewed', {
    ...(data || {}),
    timestamp,
  });
};

/**
 * Student has the Refund Request tab visible in the profile sidebar
 * (PostHog flag active). Distinct from opening the tab content.
 */
export const postHogProfileRefundTabAvailable = (
  posthog,
  data: Record<string, unknown>
) => {
  if (!posthog) return;
  posthog.capture('profile_refund_tab_available', {
    ...data,
    timestamp,
  });
};

/** User opened the Refund Request panel (content mounted). */
export const postHogProfileRefundSectionViewed = (
  posthog,
  data?: Record<string, unknown>
) => {
  if (!posthog) return;
  posthog.capture('profile_refund_section_viewed', {
    ...(data || {}),
    timestamp,
  });
};

export type TIgcseGroupClassBannerAnalyticsPayload = {
  userId?: string;
  role?: string;
  featureFlag: string;
  featureFlagVariant: string | boolean | undefined;
  countryCode?: string;
  timezone?: string;
};

type TPosthogClient = {
  capture: (
    event: string,
    props?: Record<string, unknown>,
    options?: Record<string, unknown>
  ) => void;
  flush?: () => void | Promise<void>;
};

type TPosthogWithLoaded = TPosthogClient & { __loaded?: boolean };

/** Prefer React hook client; fall back to posthog-js singleton (same instance as PostHogProvider). */
const resolveIgcsePosthogClient = (
  hookClient: TPosthogClient | undefined
): TPosthogClient | undefined => {
  if (hookClient && typeof hookClient.capture === 'function') {
    return hookClient;
  }
  const singleton = (posthogJs as unknown) as TPosthogWithLoaded;
  if (typeof singleton.capture === 'function') {
    return singleton;
  }
  return undefined;
};

const flushPosthogClient = (client: TPosthogClient) => {
  if (typeof client.flush !== 'function') {
    return;
  }
  const result = client.flush();
  if (result != null && typeof (result as Promise<void>).then === 'function') {
    (result as Promise<void>).catch(() => undefined);
  }
};

/** PostHog `capture` third-argument options (SDK uses snake_case keys). */
const POSTHOG_CAPTURE_IMMEDIATE = JSON.parse(
  '{"send_instantly":true}'
) as Record<string, unknown>;

/** Student saw the IGCSE group-class promo banner (dashboard). */
export const postHogIgcseGroupClassBannerShown = (
  posthogFromHook: TPosthogClient | undefined,
  data: TIgcseGroupClassBannerAnalyticsPayload
): boolean => {
  const ph = resolveIgcsePosthogClient(posthogFromHook);
  if (!ph) return false;
  const loaded = (ph as TPosthogWithLoaded).__loaded;
  if (!loaded) return false;
  const result = ph.capture(
    'igcse_group_class_banner_shown',
    {
      userId: data.userId,
      role: data.role,
      featureFlag: data.featureFlag,
      featureFlagVariant: data.featureFlagVariant,
      countryCode: data.countryCode,
      timezone: data.timezone,
      timestamp,
    },
    POSTHOG_CAPTURE_IMMEDIATE
  );
  if (result === undefined) return false;
  flushPosthogClient(ph);
  return true;
};

/** Student clicked Register here on the IGCSE group-class banner. */
export const postHogIgcseGroupClassBannerRegisterClicked = (
  posthogFromHook: TPosthogClient | undefined,
  data: TIgcseGroupClassBannerAnalyticsPayload
): boolean => {
  const ph = resolveIgcsePosthogClient(posthogFromHook);
  if (!ph) return false;
  const loaded = (ph as TPosthogWithLoaded).__loaded;
  if (!loaded) return false;
  const result = ph.capture(
    'igcse_group_class_banner_register_clicked',
    {
      userId: data.userId,
      role: data.role,
      featureFlag: data.featureFlag,
      featureFlagVariant: data.featureFlagVariant,
      countryCode: data.countryCode,
      timezone: data.timezone,
      timestamp,
    },
    POSTHOG_CAPTURE_IMMEDIATE
  );
  if (result === undefined) return false;
  flushPosthogClient(ph);
  return true;
};

export const postHogTutorNoSlotsPromptShown = (
  posthog,
  data: { userId: string; dayName: string }
) => {
  posthog.capture('tutor_no_slots_prompt_shown', {
    user_id: data.userId,
    day_name: data.dayName,
    timestamp,
  });
};

export const postHogTutorNoSlotsPromptCtaClicked = (
  posthog,
  data: { userId: string; dayName: string }
) => {
  posthog.capture('tutor_no_slots_prompt_cta_clicked', {
    user_id: data.userId,
    day_name: data.dayName,
    timestamp,
  });
};

export const postHogTutorNoSlotsPromptDismissed = (
  posthog,
  data: {
    userId: string;
    dayName: string;
    dismissType: 'close_button' | 'maybe_later' | 'backdrop';
  }
) => {
  posthog.capture('tutor_no_slots_prompt_dismissed', {
    user_id: data.userId,
    day_name: data.dayName,
    dismiss_type: data.dismissType,
    timestamp,
  });
};

export const postHogSendPaymentLinkClicked = (
  posthog,
  data: {
    userId?: string;
    name?: string;
    role?: string;
    courseId: string;
  }
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('tutor_payment_request');
    posthog.capture('send_payment_link_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogPaymentLinkInvoiceSent = (
  posthog,
  data: {
    userId?: string;
    name?: string;
    role?: string;
    courseId: string;
    noOfLessons: number;
  }
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('tutor_payment_request');
    posthog.capture('payment_link_invoice_sent', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogResourceButtonClickedCoursePage = (
  posthog,
  data: {
    courseId: string;
    curriculum?: string;
    subject?: string;
  }
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('resource-redirection-course-page');
    posthog.capture('resource_button_clicked_course_page', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogPastPaperButtonClickedCoursePage = (
  posthog,
  data: {
    courseId: string;
    curriculum?: string;
    subject?: string;
  }
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('resource-redirection-course-page');
    posthog.capture('past_paper_button_clicked_course_page', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export type TChatPartyLocalTimeAnalyticsPayload = {
  userId?: string;
  name?: string;
  role?: string;
  courseId?: string;
  conversationId?: string;
  /** IANA timezone of the other party in the conversation. */
  otherPartyTimezone: string;
  /** Part of the day it is for the other party, e.g. `Late night`. */
  otherPartyPartOfDay: string;
  /** True when it is outside 08:00 - 21:00 for the other party. */
  otherPartyOutsideWorkingHours: boolean;
};

export const postHogChatPartyLocalTimeShown = (
  posthog,
  data: TChatPartyLocalTimeAnalyticsPayload
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('chat_party_local_time');
    posthog.capture('chat_party_local_time_shown', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogChatPartyLocalTimeClicked = (
  posthog,
  data: TChatPartyLocalTimeAnalyticsPayload
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('chat_party_local_time');
    posthog.capture('chat_party_local_time_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export type TChatPartyResponseTimeAnalyticsPayload = {
  userId?: string;
  name?: string;
  role?: string;
  courseId?: string;
  conversationId?: string;
  /** Median reply time of the other party, in seconds. */
  otherPartyMedianResponseSeconds: number;
  /** How many observed replies the median was computed from. */
  otherPartyResponseSampleCount: number;
  /** Bucketed speed label, e.g. `Replies quickly`. */
  otherPartyResponseSpeedLabel: string;
};

export const postHogChatPartyResponseTimeShown = (
  posthog,
  data: TChatPartyResponseTimeAnalyticsPayload
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('chat_party_response_time');
    posthog.capture('chat_party_response_time_shown', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export const postHogChatPartyResponseTimeClicked = (
  posthog,
  data: TChatPartyResponseTimeAnalyticsPayload
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag('chat_party_response_time');
    posthog.capture('chat_party_response_time_clicked', {
      ...data,
      variant,
      timestamp,
    });
  });
};

export type TAutoLessonAnalysisAnalyticsPayload = {
  userId?: string;
  name?: string;
  role?: string;
  courseId?: string;
  courseTitle?: string;
  studentId?: string;
  /** Which of the three auto analysis switches this event came from. */
  surface: TAutoAnalysisSurface;
};

/** Fired once per surface when the auto analysis switch is rendered. */
export const postHogAutoLessonAnalysisShown = (
  posthog,
  data: TAutoLessonAnalysisAnalyticsPayload
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag(AUTO_LESSON_ANALYSIS_FEATURE_FLAG);
    posthog.capture('auto_lesson_analysis_shown', {
      ...data,
      variant,
      timestamp,
    });
  });
};

/**
 * Fired every time a student flips the switch. `enabled` is the value they
 * moved it to, so opt-in and opt-out rates come off this one event.
 */
export const postHogAutoLessonAnalysisToggled = (
  posthog,
  data: TAutoLessonAnalysisAnalyticsPayload & { enabled: boolean }
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag(AUTO_LESSON_ANALYSIS_FEATURE_FLAG);
    posthog.capture('auto_lesson_analysis_toggled', {
      ...data,
      variant,
      timestamp,
    });
  });
};

/**
 * The course-page switch flips optimistically and saves in the background.
 * Without this, a failed save would still be counted as an opt-in by
 * `auto_lesson_analysis_toggled` and quietly skew the numbers.
 */
export const postHogAutoLessonAnalysisSaveFailed = (
  posthog,
  data: TAutoLessonAnalysisAnalyticsPayload & { enabled: boolean }
) => {
  if (!posthog) return;
  posthog.onFeatureFlags(() => {
    const variant = posthog.getFeatureFlag(AUTO_LESSON_ANALYSIS_FEATURE_FLAG);
    posthog.capture('auto_lesson_analysis_save_failed', {
      ...data,
      variant,
      timestamp,
    });
  });
};
